import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import moment from "moment";
import { FC, useCallback, useState } from "react";
import { BsFuelPump } from "react-icons/bs";
import { FaCameraRetro, FaGasPump, FaMapMarkerAlt } from "react-icons/fa";
import { FaCalendarDays, FaMoneyBill1, FaUser } from "react-icons/fa6";
import { HiOutlineEye } from "react-icons/hi2";
import { MdOutlineQueryStats } from "react-icons/md";
import { PiCaretLeftBold, PiCaretRightBold } from "react-icons/pi";
import ReactPaginate from "react-paginate";
import { useParams } from "react-router-dom";
import { ActionIcon, Button, Select, SelectOption, Text, Tooltip } from "rizzui";
import { toast } from "sonner";
import { FormDrawerView } from "../../../../../components/forms/FormDrawer";
import RefuelDetailModal from "../../../../../components/forms/RefuelDetailModal";
import { usePrivilege } from "../../../../../components/priviledge/PriviledgeProvider";
import { useFiltersPR } from "../../../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../../../components/table/components/TableFilter";
import useFetch from "../../../../../hooks/useFetch";
import { RefuelService } from "../../../../../services/refuels/refuelService";
import { Refuel, RefuelsApiResponse } from "../../../../../type/entities/refuel-type";
import cn from "../../../../../utils/classNames";
import RefuelForm from "../../../../refuels/components/RefuelForm";
import VehicleRefuelingFilters from "./VehicleRefuelingFilters";

const VehicleRefueling: FC = () => {
    const { id } = useParams<{ id: string }>();
    const { userCan } = usePrivilege();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRefuel, setSelectedRefuel] = useState<Refuel | null>(null);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [refuelForDetail, setRefuelForDetail] = useState<Refuel | null>(null);

    const service = new RefuelService();

    // HOOKS
    const { filters, updateFilters, resetFilters, updatePageSize, updatePage, updateFilterOrder } = useFiltersPR();

    // Fetch refuel data for this vehicle
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id) return null;
        const response = await service.listRefuels(filters);
        return response.getResponseData() as RefuelsApiResponse;
    }, [filters]));

    const handleAddRefuel = useCallback(() => {
        setSelectedRefuel(null);
        setIsModalOpen(true);
    }, []);

    const handleEditRefuel = useCallback((refuel: Refuel) => {
        setSelectedRefuel(refuel);
        setIsModalOpen(true);
    }, []);

    const handleDelete = useCallback(async (refuelId: string) => {
        try {
            await service.deleteRefuel(refuelId);
            toast.success('Repostaje eliminado correctamente');
            refetch();
        } catch (error) {
            toast.error('Error al eliminar el repostaje');
        }
    }, [refetch]);

    const refuelHistory = data?.data || [];

    return (
        <div className="space-y-6 overflow-auto">
            {/* Header con estadísticas */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between  gap-4 flex-wrap xl:flex-nowrap">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-blue-100 rounded-lg">
                            <MdOutlineQueryStats className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Listado de Repostajes</h3>
                            <p className="text-sm text-gray-600">Resumen del historial de combustible</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 flex-wrap xl:flex-nowrap">
                            <Text className="text-sm text-gray-600">Mostrar:</Text>
                            <Select
                                options={[
                                    { value: 5, label: '5' },
                                    { value: 10, label: '10' },
                                    { value: 25, label: '25' },
                                    { value: 50, label: '50' },
                                    { value: 100, label: '100' }
                                ]}
                                className="w-[70px] bg-white"
                                value={filters.limit}
                                onChange={(v: SelectOption) => updatePageSize(v)}
                                selectClassName="font-semibold text-sm ring-0 shadow-sm h-9"
                                dropdownClassName="font-medium [&_li]:justify-center [&_li]:text-sm"
                            />
                        </div>
                        <Button
                            variant="outline"
                            onClick={() => setOpenFilters(!openFilters)}
                            className="h-9 pe-3 ps-3"
                        >
                            Filtros
                        </Button>
                        {userCan('create', 'refuel') && (
                            <Button
                                onClick={handleAddRefuel}
                                className="bg-primary text-white px-6 py-2.5 font-medium"
                            >
                                Nuevo Repostaje
                            </Button>
                        )}
                    </div>
                </div>

            </div>

            {/* Refueling History List */}
            {refuelHistory.length === 0 ? (
                <div className="p-8">
                    <div className="text-center py-16">
                        <div className="text-gray-400 text-lg mb-2">
                            No hay registros de repostajes
                        </div>
                        <p className="text-gray-500">
                            Los repostajes registrados aparecerán aquí
                        </p>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="space-y-4">
                        {refuelHistory.map((record: Refuel) => (
                            <div
                                key={record.id}
                                className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-4 flex-wrap xl:flex-nowrap">
                                        <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                                            <span className="text-white text-3xl font-bold"><BsFuelPump /></span>
                                        </div>
                                        <div>
                                            <h4 className="text-xl font-semibold text-gray-900">
                                                Repostaje - {record.liters}L
                                            </h4>
                                        </div>

                                        {/* Photos indicator */}
                                        {record.photoCount != null && (
                                            <div className="ms-4 flex items-center gap-2 text-sm text-gray-400">
                                                <FaCameraRetro />
                                                <span>{record.photoCount} foto{record.photoCount > 1 || record.photoCount === 0 ? 's' : ''} adjunta{record.photoCount > 1 || record.photoCount === 0 ? 's' : ''}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex gap-2">
                                        <Tooltip content="Ver detalles">
                                            <ActionIcon
                                                size="sm"
                                                variant="outline"
                                                className="hover:bg-blue-50"
                                                onClick={() => {
                                                    setRefuelForDetail(record);
                                                    setDetailModalOpen(true);
                                                }}
                                            >
                                                <HiOutlineEye className="h-4 w-4" />
                                            </ActionIcon>
                                        </Tooltip>
                                        {/* {userCan('edit', 'refuel') && (
                                            <Tooltip content="Editar">
                                                <ActionIcon
                                                    size="sm"
                                                    variant="outline"
                                                    className="hover:bg-yellow-50"
                                                    onClick={() => handleEditRefuel(record)}
                                                >
                                                    <HiOutlinePencil className="h-4 w-4" />
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                        {userCan('delete', 'refuel') && (
                                            <DeletePopover
                                                title="¿Eliminar repostaje?"
                                                description="Esta acción no se puede deshacer."
                                                onDelete={() => handleDelete(record.id)}
                                            />
                                        )} */}
                                    </div>
                                </div>

                                {/* Description */}
                                {record.description && (
                                    <div className="mb-6">
                                        <p className="text-gray-700 leading-relaxed bg-gray-50 p-2 rounded-lg">
                                            {record.description}
                                        </p>
                                    </div>
                                )}

                                {/* Details Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                                        <div className="flex justify-between">
                                            <div className="flex item-center gap-2 mb-2">
                                                <FaCalendarDays className="h-4 w-4 text-blue-500" />
                                                <span className="text-sm font-medium text-gray-700">Fecha</span>
                                            </div>
                                            <p className="text-xs text-gray-500">
                                                {formatDistance(new Date(record.date.date), new Date(), { addSuffix: true, locale: es })}
                                            </p>
                                        </div>
                                        <p className="text-lg font-semibold text-gray-900">
                                            {moment(record.date.date).format('DD/MM/YYYY')}
                                        </p>
                                    </div>

                                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                                        <div className="flex justify-between">
                                            <div className="flex item-center gap-2 mb-2">
                                                <FaMoneyBill1 className="h-4 w-4 text-green-500" />
                                                <span className="text-sm font-medium text-gray-700">Costo Total</span>
                                            </div>
                                            <p className="text-xs text-gray-500">
                                                €{(record?.totalCost / record?.liters).toFixed(2) || '0.000'}/L
                                            </p>
                                        </div>
                                        <p className="text-lg font-semibold text-gray-900">
                                            {record?.totalCost || '0.00'}€
                                        </p>
                                    </div>


                                    {record.liters && (
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <div className={cn(record.totalKilometers ? "grid grid-cols-2" : "grid grid-cols-1")}>
                                                <div>
                                                    <div className="flex justify-between">
                                                        <div className="flex item-center gap-2 mb-2">
                                                            <FaGasPump className="h-4 w-4 text-yellow-500" />
                                                            <span className="text-sm font-medium text-gray-700">Litros</span>
                                                        </div>
                                                    </div>
                                                    <p className="text-lg font-semibold text-gray-900">
                                                        {record.liters.toLocaleString()} L
                                                    </p>
                                                </div>
                                                {record.totalKilometers && (
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <FaMapMarkerAlt className="h-4 w-4 text-red-500" />
                                                            <span className="text-sm font-medium text-gray-700">Kilómetros</span>
                                                        </div>
                                                        <p className="text-lg font-semibold text-gray-900">
                                                            {record.totalKilometers.toLocaleString()} km
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {record.user && (
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <div className="flex justify-between flex-wrap xl:flex-nowrap">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <FaUser className="h-4 w-4 text-purple-500" />
                                                    <span className="text-sm font-medium text-gray-700">Usuario</span>
                                                </div>
                                                <p className="text-xs text-gray-500">
                                                    {record.user.email}
                                                </p>
                                            </div>
                                            <p className="text-lg font-semibold text-gray-900">
                                                {record.user.name} {record.user?.lastName}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                        <div className="flex flex-1 justify-between sm:hidden">
                            <Button
                                variant="outline"
                                disabled={filters.page <= 1}
                                onClick={() => updatePage({ selected: filters.page - 2 })}
                            >
                                Anterior
                            </Button>
                            <Button
                                variant="outline"
                                disabled={filters.page >= Math.ceil((data?.totalRegisters || 0) / filters.limit)}
                                onClick={() => updatePage({ selected: filters.page })}
                            >
                                Siguiente
                            </Button>
                        </div>
                        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                            <div className="flex items-center gap-2">
                                <Text className="text-sm text-gray-600">Mostrar:</Text>
                                <Select
                                    options={[
                                        { value: 5, label: '5' },
                                        { value: 10, label: '10' },
                                        { value: 25, label: '25' },
                                        { value: 50, label: '50' },
                                        { value: 100, label: '100' }
                                    ]}
                                    className="w-[70px] bg-white"
                                    value={filters.limit}
                                    onChange={(v: SelectOption) => updatePageSize(v)}
                                    selectClassName="font-semibold text-sm ring-0 shadow-sm h-9"
                                    dropdownClassName="font-medium [&_li]:justify-center [&_li]:text-sm"
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-2 rounded-xl">
                                <ReactPaginate
                                    pageCount={Math.ceil((data.totalRegisters || 0) / filters.limit)}
                                    pageRangeDisplayed={2}
                                    initialPage={filters.page <= 1 ? 0 : filters.page - 1}
                                    onPageChange={(page) => updatePage(page)}
                                    nextLabel={<PiCaretRightBold />}
                                    previousLabel={<PiCaretLeftBold />}
                                    className="flex items-center justify-center space-x-1 text-gray-700"
                                    pageClassName="inline-flex"
                                    pageLinkClassName="inline-flex items-center px-3 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary rounded-md"
                                    activeClassName=""
                                    activeLinkClassName="pagination-active"
                                    previousClassName="inline-flex"
                                    previousLinkClassName="inline-flex items-center px-1.5 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary rounded-md"
                                    nextClassName="inline-flex"
                                    nextLinkClassName="inline-flex items-center px-1.5 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary rounded-md"
                                    breakClassName="inline-flex"
                                    breakLinkClassName="inline-flex items-center px-1.5 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary rounded-md"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filter Drawer */}
            <FilterDrawerView
                isOpen={openFilters}
                setOpenDrawer={setOpenFilters}
                drawerTitle="Filtros de Repostajes"
                onClear={() => resetFilters(50)}
            >
                <VehicleRefuelingFilters
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                />
            </FilterDrawerView>

            {/* Detail Modal */}
            <RefuelDetailModal
                isOpen={detailModalOpen}
                onClose={() => {
                    setDetailModalOpen(false);
                    setRefuelForDetail(null);
                }}
                refuel={refuelForDetail}
            />

            {/* Form Drawer */}
            <FormDrawerView
                isOpen={isModalOpen}
                setOpenDrawer={setIsModalOpen}
                drawerTitle={selectedRefuel ? 'Editar Repostaje' : 'Nuevo Repostaje'}
            >
                <RefuelForm
                    refuel={selectedRefuel}
                    setOpenModal={setIsModalOpen}
                    refetch={refetch}
                    vehicleId={id}
                />
            </FormDrawerView>
        </div>
    );
};

export default VehicleRefueling;